// Plan Context Banner Component
// Displays the current plan context for Q&A sessions

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton
} from '@mui/material';
import {
  GpsFixed as GoalIcon,
  Assignment as ActivityIcon,
  Lightbulb as TipIcon,
  RecordVoiceOver as OwnerSharingIcon,
  Help as GeneralIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PlanContextBannerProps, PlanContextType } from '../types/planQa';

const PlanContextBanner: React.FC<PlanContextBannerProps & { onClose?: () => void }> = ({
  planId,
  day,
  contextType,
  planTitle,
  planDescription,
  contextElement,
  onClose
}) => {
  const { t } = useTranslation();

  const getContextIcon = (type: PlanContextType) => {
    const iconProps = { sx: { mr: 1, fontSize: '1.2rem' } };
    
    switch (type) {
      case 'goal':
        return <GoalIcon {...iconProps} />;
      case 'activity':
        return <ActivityIcon {...iconProps} />;
      case 'tip':
        return <TipIcon {...iconProps} />;
      case 'owner_sharing':
        return <OwnerSharingIcon {...iconProps} />;
      default:
        return <GeneralIcon {...iconProps} />;
    }
  };

  const getContextEmoji = (type: PlanContextType) => {
    switch (type) {
      case 'goal':
        return '🎯';
      case 'activity':
        return '📝';
      case 'tip':
        return '💡';
      case 'parent_sharing':
        return '🗣️';
      default:
        return '❓';
    }
  };

  const getContextLabel = (type: PlanContextType) => {
    switch (type) {
      case 'goal':
        return t('goal');
      case 'activity':
        return t('activity');
      case 'tip':
        return t('tip');
      case 'parent_sharing':
        return t('parentSharing');
      default:
        return t('general');
    }
  };

  const getContextColor = (type: PlanContextType) => {
    switch (type) {
      case 'goal':
        return 'primary';
      case 'activity':
        return 'secondary';
      case 'tip':
        return 'warning';
      case 'parent_sharing':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 1.25, 
        mb: 1, 
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`,
        border: (theme) => `1px solid ${theme.palette.primary.light}`,
        borderRadius: 2,
        position: 'relative'
      }}
    >
      {/* Close button */}
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{ 
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary'
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      {/* Main banner content */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {/* Context emoji/icon */}
        <Box sx={{ 
          fontSize: '1.5rem', 
          lineHeight: 1,
          mt: 0.25,
          display: 'flex',
          alignItems: 'center'
        }}>
          {getContextEmoji(contextType)}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title with day */}
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 0.5
            }}
          >
            {`${t('day')} ${day}${t('daySuffix')}: ${planTitle}`}
          </Typography>

          {/* Context type chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Chip
              icon={getContextIcon(contextType)}
              label={`${t('qaAbout')} ${getContextLabel(contextType)}`}
              color={getContextColor(contextType) as any}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          </Box>

          {/* Specific element being discussed */}
          {contextElement && (
            <Box sx={{ mb: 0.75 }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                {`${t('youAreAskingAbout')}: `}
                <strong>{contextElement}</strong>
              </Typography>
            </Box>
          )}

          {/* Plan description */}
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {planDescription}
          </Typography>
        </Box>
      </Box>

      {/* Bottom accent line */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          borderRadius: '0 0 8px 8px'
        }}
      />
    </Paper>
  );
};

export default PlanContextBanner;
